import { useEffect } from 'react'
import { createLogger } from '@/lib/logger'
import { getModuleNavigationPath } from '@/lib/utils/field-navigation'
import { focusMainWindow, isTauri } from '@/lib/utils/tauri'
import {
  resolveLiveCaptionsEnabled,
  startRecording,
  stopRecording,
} from '@/lib/utils/tauri-recording'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingStore } from '@/store/recording.store'
import { getOrgPath } from '@/utils/org-paths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type {
  AssistantClientActionPayload,
  AssistantNavigatePage,
} from '@scrambl/shared'
import { ModuleTypeEnum, SocketEvent } from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'

const logger = createLogger('realtime/useAssistantClientActions')

/**
 * Executes device-local actions dispatched by Benny's server-side tools
 * (currently meeting recording start/stop — the recorder lives in the Tauri
 * desktop app, out of the server's reach). Mount once in the desktop main
 * window; the pet/companion window never mounts this, so actions run exactly
 * once no matter which chat surface asked for them.
 */
/**
 * Named destinations Benny can open, mapped to their org-scoped routes.
 * Exported: Simple Mode's canvas `page` directive resolves the same names, so
 * "open my timesheets" lands on the same route whether it opens the full
 * window (this file) or embeds in the canvas.
 */
export const PAGE_ROUTES: Record<AssistantNavigatePage, string> = {
  today: '/today',
  'my-tasks': '/tasks/mine',
  'my-time': '/my-time',
  tasks: '/tasks',
  projects: '/projects',
  clients: '/entities',
  contacts: '/contacts',
  leads: '/leads',
  deals: '/deals',
  tickets: '/tickets',
  calendar: '/calendar',
  focus: '/focus',
  notes: '/notes',
  documents: '/documents',
  invoices: '/invoices',
  contracts: '/contracts',
  expenses: '/my-time/expenses',
  notifications: '/notifications',
  threads: '/threads',
  forms: '/forms',
  settings: '/settings',
}

/** Resolve an assistant navigate action to an in-app route. */
function resolveNavigatePath(
  payload: AssistantClientActionPayload,
  orgSlug: string,
): string | null {
  if (payload.page) {
    const base = PAGE_ROUTES[payload.page]
    return base ? getOrgPath(orgSlug, base) : null
  }
  if (!payload.moduleType || !payload.recordId) return null
  const moduleType = payload.moduleType.toUpperCase()
  if (!(moduleType in ModuleTypeEnum)) return null
  return getModuleNavigationPath(
    ModuleTypeEnum[moduleType as keyof typeof ModuleTypeEnum],
    payload.recordId,
    orgSlug,
  )
}

export function useAssistantClientActions() {
  const { socket, isReady } = useRealtime({ enableBroadcastChannel: false })
  const navigate = useNavigate()

  useEffect(() => {
    if (!socket || !isReady) return undefined

    const handleAction = async (payload: AssistantClientActionPayload) => {
      logger.info('assistant client action received', payload?.action)
      switch (payload?.action) {
        case 'navigate': {
          const orgSlug =
            useOrganizationStore.getState().currentOrganization?.slug
          if (!orgSlug) return
          const path = resolveNavigatePath(payload, orgSlug)
          if (!path) {
            logger.warn('unresolvable navigate action', payload)
            return
          }
          // The request can come from the pet window; bring the app forward.
          if (isTauri()) void focusMainWindow()
          navigate(path)
          return
        }
        case 'recording.start': {
          if (!isTauri()) return
          const { state } = useRecordingStore.getState()
          if (state === 'recording' || state === 'paused') {
            toast.info('A recording is already running.')
            return
          }
          try {
            // Honor the remembered Live Captions preference, same as the
            // Record button and calendar auto-start paths.
            const liveCaptions = await resolveLiveCaptionsEnabled()
            await startRecording({ liveCaptions })
            toast.success(
              payload.title
                ? `Benny started recording "${payload.title}"…`
                : 'Benny started recording…',
            )
          } catch (error) {
            logger.error('assistant-triggered recording start failed', error)
            toast.error("Benny couldn't start recording", {
              description:
                'Use the Record button in the title bar — you may need to grant microphone or system-audio access first.',
            })
          }
          return
        }
        case 'recording.stop': {
          const { state, controls } = useRecordingStore.getState()
          if (state !== 'recording' && state !== 'paused') {
            // Store may lag Rust state briefly — attempt a quiet direct stop.
            if (isTauri()) await stopRecording().catch(() => {})
            return
          }
          try {
            if (controls) await controls.stop()
            else if (isTauri()) await stopRecording()
            toast.success('Recording stopped — generating meeting notes…')
          } catch (error) {
            logger.error('assistant-triggered recording stop failed', error)
            toast.error("Benny couldn't stop the recording.")
          }
          return
        }
      }
    }

    const listener = (payload: AssistantClientActionPayload) => {
      void handleAction(payload)
    }
    socket.on(SocketEvent.ASSISTANT_CLIENT_ACTION, listener)
    return () => {
      socket.off(SocketEvent.ASSISTANT_CLIENT_ACTION, listener)
    }
    // navigate is stable (react-router useNavigate identity)
  }, [socket, isReady, navigate])
}
